import { useEffect, useState, type FormEvent } from 'react';
import { Alert, Button, FormField, Input, Surface } from '@nexvia-logistics/ui';
import { PackageIcon } from '@/shared/icons';
import { describeStorageProfileError, updateStorageProfile, type StorageProfile } from './storage-api';
import { FIELD_MAX_LENGTH, isValidContactEmail } from './storage-profile-fields';

export interface QuoteContactFormProps {
  profile: StorageProfile;
  onSaved: (updated: StorageProfile) => void;
}

/**
 * NEX-1164 — "datos de contacto para recibir las solicitudes a cotizar": the email that gets the
 * new-request notice (NEX-1328, `storage_new_request`) and the phone shown with it — the API's
 * `contactEmail`/`contactPhone`. Unlike the login email (the Firebase identity, never editable),
 * these are editable in EVERY account state, also once verified (the fiscal data turns read-only
 * then; where to receive quote requests does not). Blank = `null` = the API falls back to the
 * login email/phone, which the hint names so the depósito knows where the notices go.
 *
 * Same composition as `FiscalDataForm`'s editable branch: `Surface` card, `FormField`+`Input`
 * (with `invalid` for the critical ring), `Alert` for the API error, a `role="status"` line once
 * saved.
 *
 * **NEX-1164 design pass (2026-10-18, spec §3.6, D-6.2)**: this is the mock's "Avisos" card. The
 * mock's checkbox ("Avisarme por mail cuando llegue una solicitud de mi zona") has no backend — the
 * mail always goes out — so the card shows where it goes instead: the same two fields and the same
 * `PATCH /storage/me`, with the mock's title, the `Package` glyph and the labels renamed.
 */
export function QuoteContactForm({ profile, onSaved }: QuoteContactFormProps) {
  const [contactEmail, setContactEmail] = useState(profile.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(profile.contactPhone ?? '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setContactEmail(profile.contactEmail ?? '');
    setContactPhone(profile.contactPhone ?? '');
  }, [profile.contactEmail, profile.contactPhone]);

  const clearTransientState = () => {
    setSaved(false);
    setErrorMessage(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const email = contactEmail.trim();
    const phone = contactPhone.trim();
    if (email && !isValidContactEmail(email)) {
      setEmailError('Ese correo no es válido.');
      return;
    }
    setEmailError(null);

    setSaving(true);
    setErrorMessage(null);
    try {
      const result = await updateStorageProfile({ contactEmail: email || null, contactPhone: phone || null });
      onSaved(result.storageProfile);
      setSaved(true);
    } catch (err) {
      setErrorMessage(describeStorageProfileError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Surface as="section" variant="outlined" padding="md">
      <h2 className="m-0 flex items-center gap-2 text-ui-h3 text-on-surface">
        <PackageIcon size={20} className="shrink-0 text-primary" />
        Avisos
      </h2>
      <p className="m-0 mt-1 text-ui-body text-on-surface-variant">
        Te avisamos por mail cuando llega una solicitud de tu zona. Si dejás un campo vacío, usamos el de tu cuenta.
      </p>

      <form onSubmit={(event) => void handleSubmit(event)} className="mt-4 flex flex-col gap-4" noValidate>
        <FormField label="Correo para avisos" hint={<span className="wrap-anywhere">Si lo dejás vacío: {profile.email}</span>} error={emailError}>
          {(field) => (
            <Input
              {...field}
              invalid={Boolean(emailError)}
              type="email"
              autoComplete="email"
              maxLength={FIELD_MAX_LENGTH.contactEmail}
              value={contactEmail}
              onChange={(event) => {
                setContactEmail(event.target.value);
                clearTransientState();
              }}
            />
          )}
        </FormField>

        <FormField label="Teléfono para avisos" hint={profile.phone ? `Si lo dejás vacío: ${profile.phone}` : undefined}>
          {(field) => (
            <Input
              {...field}
              type="tel"
              autoComplete="tel"
              maxLength={FIELD_MAX_LENGTH.contactPhone}
              value={contactPhone}
              onChange={(event) => {
                setContactPhone(event.target.value);
                clearTransientState();
              }}
            />
          )}
        </FormField>

        {errorMessage && <Alert tone="critical">{errorMessage}</Alert>}
        {saved && !errorMessage && (
          <p role="status" className="m-0 text-ui-body text-success-base">
            Contacto guardado.
          </p>
        )}

        <Button type="submit" variant="primary" size="xs" showArrow={false} disabled={saving} className="self-start">
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </form>
    </Surface>
  );
}
